import {DateTime} from 'luxon';

/**
 * Formats a date-time string in a given timezone, defaulting to local time if timeZone is not provided.
 * @param {string} isoDateTime - The ISO date-time string.
 * @param {string} timeZone - IANA timezone (e.g., "America/New_York").
 * @param {string} format - Desired output format (default is 'MM/dd/yyyy HH:mm:ss').
 * @returns {string} - Formatted date-time string.
 */
export const formatDateTimeInTimeZone = (isoDateTime, timeZone, format = 'MM/dd/yyyy hh:mm:ss a') => {
    try {
        if (!isoDateTime) return "-";
        const dt = DateTime.fromISO(isoDateTime, { zone: 'utc' });
        if (!dt.isValid) throw new Error('Invalid date-time');

        const formattedDateTime = timeZone
            ? dt.setZone(timeZone).toFormat(format)
            : dt.toLocal().toFormat(format); // Use local time if timeZone is not provided

        return formattedDateTime;
    } catch (error) {
        console.error('Error formatting date-time:', error);
        return "-";
    }
};

export const formatDateTimeInTimeZoneInISO = (isoDateTime, timeZone) => {
    try {
        if (!isoDateTime) return "-";
        const dt = DateTime.fromISO(isoDateTime, { zone: 'utc' });
        if (!dt.isValid) throw new Error('Invalid date-time');

      return timeZone
          ? dt.setZone(timeZone, { keepCalendarTime: true }).plus({ hours: 1 }).toISO()
          : dt.toLocal().toISO();
    } catch (error) {
        console.error('Error formatting date-time:', error);
        return "";
    }
};

/**
 * Formats a date-time string in a given timezone, returning an object with separate date and time fields.
 * @param {string} isoDateTime - The ISO date-time string (e.g., "2025-05-19T12:34:56Z").
 * @param {string} timeZone - IANA timezone (e.g., "America/New_York"). If not provided, uses local timezone.
 * @returns {Object} - Object with formatted date and time (e.g., { date: "05/19/2025", time: "08:34:56 AM" }).
 */
export const formatDateTimeInTimeZoneObject = (isoDateTime, timeZone) => {
  try {
    if (!isoDateTime) return { date: "-", time: "-" };

    const dt = DateTime.fromISO(isoDateTime, { zone: 'utc' });
    if (!dt.isValid) throw new Error('Invalid date-time');

    const targetZone = timeZone ? timeZone : 'local';
    const formatted = dt.setZone(targetZone);

    return {
      date: formatted.toFormat('MM/dd/yyyy'),
      time: formatted.toFormat('hh:mm:ss a')
    };
  } catch (error) {
    console.error('Error formatting date-time:', error);
    return { date: "-", time: "-" };
  }
};